import { motion } from 'framer-motion';

const MissSpark = ({ mood = 'neutral', size = 'medium', speaking = false }) => {
  const sizeClasses = {
    small: 'w-24 h-24',
    medium: 'w-40 h-40',
    large: 'w-64 h-64',
  };

  const getMouthPath = () => {
    switch (mood) {
      case 'happy': return 'M70 125 Q100 150 130 125';
      case 'excited': return 'M65 120 Q100 160 135 120';
      case 'singing': return 'M80 125 Q100 145 120 125 Q100 135 80 125';
      case 'encouraging': return 'M75 125 Q100 145 125 125';
      default: return 'M80 128 Q100 140 120 128';
    }
  };

  const getEyeShape = () => {
    if (mood === 'excited' || mood === 'happy') {
      return { leftY: 85, rightY: 85, size: 8 };
    }
    if (mood === 'singing') {
      return { leftY: 88, rightY: 88, size: 7 };
    }
    return { leftY: 88, rightY: 88, size: 7 };
  };

  const eyes = getEyeShape();

  return (
    <div className={`${sizeClasses[size]} mx-auto`}>
      <motion.svg 
        viewBox="0 0 200 200" 
        className="w-full h-full drop-shadow-lg"
        animate={speaking ? { 
          scale: [1, 1.03, 1],
          rotate: [0, 1, -1, 0]
        } : {}}
        transition={{ duration: 0.4, repeat: speaking ? Infinity : 0 }}
      >
        {/* Background Circle */}
        <circle cx="100" cy="100" r="95" fill="#FF6B9D" />
        <circle cx="100" cy="100" r="88" fill="#FF8FB1" />
        
        {/* Face */}
        <circle cx="100" cy="95" r="55" fill="#F5D0C5" />
        
        {/* Hair */}
        <ellipse cx="100" cy="50" rx="50" ry="25" fill="#4A3728" />
        <ellipse cx="60" cy="65" rx="20" ry="30" fill="#4A3728" />
        <ellipse cx="140" cy="65" rx="20" ry="30" fill="#4A3728" />
        
        {/* Hair highlights */}
        <path d="M70 40 Q80 30 90 40" fill="none" stroke="#6B4E3D" strokeWidth="3" />
        <path d="M110 40 Q120 30 130 40" fill="none" stroke="#6B4E3D" strokeWidth="3" />
        
        {/* Hair accessories - bows */}
        <circle cx="55" cy="55" r="8" fill="#FFE66D" />
        <circle cx="145" cy="55" r="8" fill="#FFE66D" />
        
        {/* Eyes */}
        <motion.ellipse 
          cx="75" 
          cy={eyes.leftY} 
          rx={eyes.size} 
          ry={eyes.size + 2}
          fill="#2D3748"
          animate={mood === 'singing' ? { ry: [9, 7, 9] } : {}}
          transition={{ duration: 0.5, repeat: Infinity }}
        />
        <motion.ellipse 
          cx="125" 
          cy={eyes.rightY} 
          rx={eyes.size} 
          ry={eyes.size + 2}
          fill="#2D3748"
          animate={mood === 'singing' ? { ry: [9, 7, 9] } : {}}
          transition={{ duration: 0.5, repeat: Infinity }}
        />
        
        {/* Eye sparkles */}
        <circle cx="78" cy="85" r="3" fill="white" />
        <circle cx="128" cy="85" r="3" fill="white" />
        <circle cx="73" cy="90" r="1.5" fill="white" />
        <circle cx="123" cy="90" r="1.5" fill="white" />
        
        {/* Eyelashes */}
        <path d="M65 80 L62 75 M68 78 L64 73 M72 77 L70 72" stroke="#2D3748" strokeWidth="1.5" />
        <path d="M135 80 L138 75 M132 78 L136 73 M128 77 L130 72" stroke="#2D3748" strokeWidth="1.5" />
        
        {/* Eyebrows */}
        <path d="M60 72 Q75 65 88 72" fill="none" stroke="#4A3728" strokeWidth="2" />
        <path d="M112 72 Q125 65 140 72" fill="none" stroke="#4A3728" strokeWidth="2" />
        
        {/* Nose */}
        <circle cx="100" cy="102" r="3" fill="#E8B4A8" />
        
        {/* Blush */}
        <ellipse cx="60" cy="105" rx="10" ry="6" fill="#FFB4B4" opacity="0.6" />
        <ellipse cx="140" cy="105" rx="10" ry="6" fill="#FFB4B4" opacity="0.6" />
        
        {/* Mouth */}
        <motion.path 
          d={getMouthPath()} 
          fill={mood === 'singing' || mood === 'excited' ? '#FF6B6B' : 'none'}
          stroke="#E85A71" 
          strokeWidth="3"
          strokeLinecap="round"
        />
        
        {/* Teeth for big smile */}
        {(mood === 'excited' || mood === 'singing') && (
          <path d="M85 130 L115 130" stroke="white" strokeWidth="6" strokeLinecap="round" />
        )}
        
        {/* Collar / Dress top */}
        <path d="M60 150 Q100 165 140 150" fill="#7FDBDA" />
        <path d="M70 155 L100 145 L130 155" fill="#98D8C8" />
        
        {/* Musical notes when singing */}
        {mood === 'singing' && (
          <>
            <motion.text 
              x="150" y="60" 
              fontSize="16" 
              fill="#FFE66D"
              animate={{ y: [60, 50, 60], opacity: [1, 0.5, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              ♪
            </motion.text>
            <motion.text 
              x="160" y="75" 
              fontSize="12" 
              fill="#7FDBDA"
              animate={{ y: [75, 65, 75], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            >
              ♫
            </motion.text>
          </>
        )}
        
        {/* Stars for excited */}
        {mood === 'excited' && (
          <>
            <motion.text 
              x="40" y="50" 
              fontSize="14" 
              fill="#FFE66D"
              animate={{ scale: [1, 1.2, 1], rotate: [0, 20, 0] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            >
              ★
            </motion.text>
            <motion.text 
              x="155" y="45" 
              fontSize="12" 
              fill="#FF6B9D"
              animate={{ scale: [1.2, 1, 1.2], rotate: [0, -20, 0] }}
              transition={{ duration: 0.6, repeat: Infinity }}
            >
              ★
            </motion.text>
          </>
        )}
      </motion.svg>
    </div>
  );
};

export default MissSpark;
